<template>
  <div
    class="bg-gray-450 p-4 md:w-full pl-6 pt-6 rounded-br-3xl rounded-bl-3xl transition-all"
  >
    <div class="flex flex-col">
      <div class="mb-4">
        Se realizará un cargo inicial de ${{ finalAmount }} MXN para activar tu suscripción
      </div>
      <div class="mb-2">
        Al hacer clic en <span class="font-bold">"Pagar"</span>, te llevaremos a un lugar seguro para completar la transacción. Una vez finalizado el pago, volverás automáticamente a nuestro sitio con la confirmación.
      </div>

      <!-- SDK Payment Component -->
      <card-payment ref="paymentElement" v-show="!loading">
        <template v-slot:pay-text>💳 Pagar Ahora</template>
      </card-payment>

      <!-- Loading Spinner -->
      <div v-if="loading" class="flex flex-col justify-center items-center mt-4">
        <div class="spinner"></div>
        <p class="text-sm text-gray-600 mt-3">Cargando método de pago...</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useStore } from 'vuex';
import { loadPaymentDependencies } from '../utils/paymentDependencies';

export default {
  name: 'PaymentCard',
  props: {
    // Props originales (para compatibilidad)
    information: {
      type: Object,
      default: () => ({})
    },
    url: {
      type: String,
      default: ''
    },
    // Props del SDK (opcionales)
    amount: {
      type: String,
      required: false
    },
    folio: {
      type: String,
      required: false
    },
    systemId: {
      type: String,
      default: 'DISH'
    },
    returnUrl: {
      type: String,
      default: () => {
        const stage = process.env.STAGE;
        const base = window.location.origin;
        return stage ? `${base}/${stage}/resumen` : `${base}/resumen`;
      }
    },
    cancelUrl: {
      type: String,
      default: () => {
        const stage = process.env.STAGE;
        const base = window.location.origin;
        return stage ? `${base}/${stage}/pago` : `${base}/pago`;
      }
    },
    reference: {
      type: String,
      default: 'dish_venta_nueva'
    },
    description: {
      type: String,
      default: 'Pago suscripcion DISH'
    }
  },
  setup(props) {
    const store = useStore();
    const paymentElement = ref(null);
    const loading = ref(true);
    const orderId = ref(null);

    const folioFromStore = computed(() => {
      const folioData = store.getters.getFolio;
      return folioData?.dataObj?.folio || null;
    });

    const finalAmount = computed(() => {
      if (props.amount) return props.amount;
      if (props.information?.suscripcion) {
        return parseFloat(props.information.suscripcion).toFixed(2);
      }
      return '1.00';
    });

    const finalFolio = computed(() => {
      if (props.folio) return props.folio;
      if (folioFromStore.value) return folioFromStore.value;
      if (props.information?.folio) return props.information.folio;
      return 'B' + Date.now();
    });

    const config = computed(() => ({
      cancelUrl: props.cancelUrl,
      returnUrl: props.url,
      systemId: props.systemId,
      amount: finalAmount.value,
      folio: finalFolio.value,
      paymentType: 'TOKENIZED',
      reference: props.reference,
      description: props.description
    }));

    const handleLoading = (event) => {
      loading.value = event.detail.loading;
    };

    const handleReady = (event) => {
      console.log('✅ Ready Event:', event.detail);
      if (event.detail && event.detail.idOrder) {
        orderId.value = event.detail.idOrder;
        console.log('🆔 Order ID capturado:', orderId.value);
      }
    };

    const handleSessionSuccess = async (event) => {
      console.log('✅ Session Created:', event.detail);
      store.commit('SET_PAYMENT_METHOD', 'card');
      console.log('💳 Método de pago establecido: card');

      if (!orderId.value) {
        console.warn('⚠️ orderId aún no disponible, esperando...');
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      if (!orderId.value) {
        console.error('❌ No se pudo obtener el orderId del evento ready');
        alert('Error: No se pudo obtener el ID de orden.');
        return;
      }

      console.log('🧪 Registrando método de pago...');
      try {
        const registerData = {
          folio: folioFromStore.value,
          metodo_pago: 'Evopayment tarjeta tokenizada',
          pago_suscripcion: 'BANAMEX',
          id_order: orderId.value
        };

        console.log('📤 Datos a enviar:', registerData);

        const registerResult = await store.dispatch('setRegisterMethod', registerData);

        console.log('📋 Respuesta:', registerResult);

        if (registerResult && registerResult.dataObj) {
          if (registerResult.dataObj.codigo_respuesta === 222) {
            console.error('❌ Error:', registerResult.dataObj.mensaje_respuesta);
            alert('Error: ' + registerResult.dataObj.mensaje_respuesta);
          } else {
            console.log('✅ Método registrado exitosamente');

            const paymentData = {
              folio: finalFolio.value,
              monto: finalAmount.value,
              timestamp: Date.now(),
              paymentMethod: 'card',
              provider: 'BANAMEX',
              methodRegistered: true,
              orderId: orderId.value
            };
            localStorage.setItem('card_operation', JSON.stringify(paymentData));
            console.log('💾 Guardado en localStorage:', paymentData);
          }
        }
      } catch (error) {
        console.error('❌ Error:', error);
        alert('Error: ' + error.message);
      }

      store.commit('SET_SHIPPING_INFORMATION', {
        plazo_instalacion: 'Tu pago ha sido procesado exitosamente. En breve nos pondremos en contacto contigo para coordinar la instalación',
        orden_instalacion: null,
        mensaje_respuesta: 'Gracias por tu preferencia'
      });

      console.log('📦 Shipping info establecida');
    };

    const handleSessionError = (event) => {
      console.error('❌ Session Error:', event.detail);
      alert('Error al crear la sesión de pago. Por favor, intenta de nuevo.');
    };

    const handlePaymentSuccess = async (event) => {
      console.log('✅ Payment Success:', event.detail);
      store.commit('SET_PAYMENT_METHOD', 'card');

      const folio = folioFromStore.value;
      const orderIdFromEvent = event.detail.orderId || 'ORDER_' + Date.now();

      try {
        console.log('🏦 Registrando transacción en Banamex...');
        await store.dispatch('tranRecibidaBanamex', {
          cliente: folio,
          orderId: orderIdFromEvent,
          canalPago: 'BANAMEX',
          currentStatus: event.detail.status || 'APPROVED',
          authCode: event.detail.authCode || '',
          monto: finalAmount.value,
          reference: event.detail.reference || 'dish_venta_nueva',
          productDesc: 'Pago suscripcion DISH',
          canalOrigen: 'WEB',
          idConcepto: ''
        });
        console.log('✅ Transacción Banamex registrada exitosamente');

        console.log('⏭️ Saltando registro de método (ya registrado en handleSessionSuccess)');

        console.log('⏳ Esperando 1 segundo antes de enviar a ventas...');
        await new Promise(resolve => setTimeout(resolve, 1000));

        console.log('📤 Enviando información a ventas...');
        await store.dispatch('sendStatusShipping');
        const shippingResult = await store.dispatch('sendShipping');

        if (shippingResult) {
          console.log('✅ Información enviada a ventas exitosamente');
        } else {
          console.warn('⚠️ sendShipping no retornó resultado exitoso');
        }
      } catch (error) {
        console.error('❌ Error en el proceso de pago:', error);
      }
    };

    const handlePaymentError = (event) => {
      console.error('❌ Payment Error:', event.detail);
      alert('Error en el pago. Por favor, intenta de nuevo.');
    };

    const handleTokenSuccess = (event) => {
      console.log('✅ Token Created:', event.detail);
    };

    const handleTokenError = (event) => {
      console.error('❌ Token Error:', event.detail);
    };

    onMounted(async () => {
      store.commit('SET_PAYMENT_METHOD', 'card');
      console.log('💳 Método de pago establecido: card (al montar componente)');

      try {
        loading.value = true;
        console.log('⏳ Cargando dependencias del SDK...');
        await loadPaymentDependencies();
        console.log('✅ Dependencias cargadas');
      } catch (error) {
        console.error('❌ Error cargando dependencias:', error);
        alert('Error al cargar el sistema de pagos. Por favor, recarga la página.');
        loading.value = false;
        return;
      }

      setTimeout(() => {
        if (paymentElement.value) {
          console.log('🔧 Inicializando SDK de pagos');
          console.log('  - Amount:', finalAmount.value);
          console.log('  - Folio:', finalFolio.value);
          console.log('  - Config:', config.value);
          paymentElement.value.initialize('PURCHASE', config.value);
          loading.value = false;
          console.log('✅ SDK inicializado correctamente');
        } else {
          console.error('❌ Elemento de pago no encontrado');
          loading.value = false;
        }
      }, 100);

      window.addEventListener('loading', handleLoading);
      window.addEventListener('ready', handleReady);
      window.addEventListener('sessionSuccess', handleSessionSuccess);
      window.addEventListener('sessionError', handleSessionError);
      window.addEventListener('paymentSuccess', handlePaymentSuccess);
      window.addEventListener('paymentError', handlePaymentError);
      window.addEventListener('tokenSuccess', handleTokenSuccess);
      window.addEventListener('tokenError', handleTokenError);
    });

    onUnmounted(() => {
      window.removeEventListener('loading', handleLoading);
      window.removeEventListener('ready', handleReady);
      window.removeEventListener('sessionSuccess', handleSessionSuccess);
      window.removeEventListener('sessionError', handleSessionError);
      window.removeEventListener('paymentSuccess', handlePaymentSuccess);
      window.removeEventListener('paymentError', handlePaymentError);
      window.removeEventListener('tokenSuccess', handleTokenSuccess);
      window.removeEventListener('tokenError', handleTokenError);
    });

    return {
      paymentElement,
      loading,
      finalAmount
    };
  }
};
</script>

<style scoped>
.spinner {
  border: 3px solid #f3f4f6;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Personalización de botones del SDK */
card-payment {
  --gdev-btn-pay-bg: #e11c27;
  --gdev-btn-pay-hover-bg: #c81923;
  --gdev-btn-radius: 8px;
}
</style>